'use client'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { useSolanaUsdcBalance } from '@/hooks/kalshi/useSolanaBalance'
import usePolygonBalances from '@/hooks/polymarket/usePolygonBalances'
import { useWallet } from '@/providers/WalletContext'
import { useModalStore } from '@/stores/modal-store'
import { copyAddress } from '@/utils/clipboard'
import { deriveSafeAddress } from '@/utils/safe'
import { ArrowLeft, Copy, Info, RefreshCw } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { QRCodeSVG } from 'qrcode.react'
import { useState } from 'react'

function truncateAddress(address: string, startChars = 6, endChars = 6): string {
	if (address.length <= startChars + endChars) {
		return address
	}
	return `${address.slice(0, startChars)}...${address.slice(-endChars)}`
}

type Chain = 'solana' | 'polygon'
type Asset = 'usdc' | 'usdce'

const CHAIN_OPTIONS = [
	{ value: 'solana' as Chain, label: 'Solana', image: '/logos/solana.png' },
	{
		value: 'polygon' as Chain,
		label: 'Polygon',
		image: '/logos/matic-logo.png',
	},
]

const ASSET_OPTIONS = {
	solana: [{ value: 'usdc' as Asset, label: 'USDC', image: '/logos/usdc.webp' }],
	polygon: [{ value: 'usdce' as Asset, label: 'USDC.e', image: '/logos/usdc.webp' }],
}

export function ManualTransferModal() {
	const { activeModal, closeModal, openModal } = useModalStore()
	const isOpen = activeModal === 'manual-transfer'
	const { eoaAddress, solanaAddress } = useWallet()
	const t = useTranslations('manualTransfer')

	const [selectedChain, setSelectedChain] = useState<Chain>('solana')
	const [selectedAsset, setSelectedAsset] = useState<Asset>('usdc')

	const safeAddress = eoaAddress ? deriveSafeAddress(eoaAddress) : undefined

	const {
		formattedUsdcBalance: polygonBalance,
		refetch: refetchPolygon,
		isLoading: polygonLoading,
	} = usePolygonBalances(safeAddress)

	const { data: solanaBalanceData, isLoading: solanaLoading } = useSolanaUsdcBalance()
	const solanaBalance = solanaBalanceData?.formatted || '0.00'

	const handleBack = () => {
		closeModal()
		openModal('deposit-primary')
	}

	const handleChainChange = (chain: Chain) => {
		setSelectedChain(chain)
		// Auto-select the first available asset for the chain
		setSelectedAsset(chain === 'solana' ? 'usdc' : 'usdce')
	}

	const currentAddress = selectedChain === 'solana' ? solanaAddress : safeAddress
	const currentBalance = selectedChain === 'solana' ? solanaBalance : polygonBalance
	const isLoading = selectedChain === 'solana' ? solanaLoading : polygonLoading

	const explorerUrl =
		selectedChain === 'solana'
			? `https://solscan.io/account/${currentAddress}`
			: `https://polygonscan.com/address/${currentAddress}`

	const minDeposit = selectedChain === 'solana' ? '5 USDC' : '5 USDC.e'

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && closeModal()}>
			<DialogContent
				closeButtonClasses="size-[24px] text-white"
				closeButtonContainerClasses="top-[15px] lg:top-[20px]  lg:right-[26px] cursor-pointer"
				className="gap-[12px] rounded-3xl border border-white/10 bg-brand-dark-light p-[18px] sm:max-w-[512px] lg:gap-[24px] lg:p-[24px]"
			>
				<DialogHeader className="relative gap-[8px]">
					<Button
						variant="ghost"
						size="icon"
						className="-ml-2 -top-[7px] absolute left-0 h-8 w-8"
						onClick={handleBack}
					>
						<ArrowLeft className="size-[24px] text-brand-gray-secondary" />
					</Button>
					<DialogTitle className="text-center font-semibold text-[18px] text-white leading-[100%]">
						{t('title')}
					</DialogTitle>
					<DialogDescription className="text-center text-[16px] text-brand-gray-secondary leading-[100%]">
						{t('description')}
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-[12px] rounded-[12px] border border-brand-gray p-[12px] lg:gap-[24px] lg:p-[24px]">
					{/* Chain and Asset Selectors */}
					<div className="grid grid-cols-2 gap-[16px]">
						<div className="flex flex-col gap-[8px]">
							<span className=" font-normal text-[14px] text-white/75 leading-[100%]">
								{t('chain')}
							</span>
							<Select value={selectedChain} onValueChange={(val: Chain) => handleChainChange(val)}>
								<SelectTrigger className="h-[48px]! w-full rounded-[8px]! border-brand-gray! bg-brand-dark-light! p-[12px] text-[14px] text-white leading-[100%]">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{CHAIN_OPTIONS.map(chain => (
										<SelectItem key={chain.value} value={chain.value}>
											<div className="flex items-center gap-2">
												<img
													src={chain.image}
													alt={chain.label}
													width={24}
													height={24}
													className="rounded-full"
												/>
												{chain.label}
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="flex flex-col gap-[8px]">
							<span className=" font-normal text-[14px] text-white/75 leading-[100%]">
								{t('asset')}
							</span>
							<Select value={selectedAsset} onValueChange={(val: Asset) => setSelectedAsset(val)}>
								<SelectTrigger className="h-[48px]! w-full rounded-[8px]! border-brand-gray! bg-brand-dark-light! p-[12px] text-[14px] text-white leading-[100%]">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{ASSET_OPTIONS[selectedChain].map(asset => (
										<SelectItem key={asset.value} value={asset.value}>
											<div className="flex items-center gap-2">
												<img
													src={asset.image}
													alt={asset.label}
													width={24}
													height={24}
													className="rounded-full"
												/>
												{asset.label}
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>

					{/* Balance Display */}
					<div className="flex flex-col gap-[8] lg:gap-[16px]">
						<span className=" font-semibold text-[16px] text-white leading-[100%]">
							{t('yourBalance')}
						</span>
						<div className="flex h-[48px] items-center justify-between gap-[12px] rounded-[8px] border border-brand-gray bg-brand-dark-light py-[12px] pr-[12px] pl-[12px] leading-0">
							<span className=" font-normal text-[14px] text-white leading-[100%]">
								${currentBalance}
							</span>
							<Button
								variant="ghost"
								size="icon"
								className="size-[24px] shrink-0 cursor-pointer"
								onClick={() => (selectedChain === 'solana' ? {} : refetchPolygon())}
								disabled={isLoading}
							>
								<RefreshCw
									className={`size-[18px] text-brand-muted ${isLoading ? 'animate-spin' : ''}`}
								/>
							</Button>
						</div>
					</div>

					{/* Address Section */}
					<div className="flex flex-col gap-[8px] lg:gap-[16px]">
						<span className=" font-semibold text-[16px] text-white leading-[100%]">
							{t('yourAddress')}
						</span>
						{currentAddress ? (
							<div className="flex h-[48px] items-center gap-[12px] rounded-[8px] border border-brand-gray bg-brand-dark-light py-[12px] pr-[12px] pl-[12px] leading-0">
								<code className="flex-1 font-normal text-[14px] text-white leading-[100%]">
									{truncateAddress(currentAddress)}
								</code>
								<Button
									variant="ghost"
									size="icon"
									className="size-[24px] shrink-0 cursor-pointer"
									onClick={() =>
										copyAddress(currentAddress, selectedChain === 'solana' ? 'Solana' : 'Polygon')
									}
								>
									<Copy strokeWidth={2} className="size-[18px] text-brand-muted" />
								</Button>
							</div>
						) : (
							<p className="text-[14px] text-brand-gray-secondary">{t('loading')}</p>
						)}
					</div>

					{/* QR Code */}
					<div className="flex items-center gap-[16px] lg:gap-[32px]">
						<div className="flex h-[100px] w-[100px] items-center justify-center rounded-[12px] bg-white p-[16px] lg:h-[160px] lg:w-[160px]">
							{currentAddress && <QRCodeSVG value={explorerUrl} size={168} />}
						</div>
						<div className="flex flex-col items-start gap-[16px]">
							<p className=" font-medium text-[14px] text-brand-gray-secondary leading-[120%]">
								{t.rich('sendFunds', {
									asset: selectedAsset === 'usdc' ? 'USDC' : 'USDC.e',
									chain: selectedChain === 'solana' ? 'Solana mainnet' : 'Polygon',
									b: chunks => <b className="text-white">{chunks}</b>,
								})}
							</p>

							<p className=" font-medium text-[14px] text-brand-gray-secondary leading-[120%]">
								{t.rich('minimum', {
									amount: minDeposit,
									b: chunks => <b className="text-white">{chunks}</b>,
								})}
							</p>
						</div>
					</div>

					{/* Info Boxes */}
					{selectedChain === 'solana' ? (
						<div className="rounded-[12px] border border-emerald-500/20 bg-emerald-500/10 p-[8px]">
							<p className=" text-[12px] text-emerald-500">{t('solanaGasSponsored')}</p>
						</div>
					) : (
						<div className="flex flex-col gap-[8px]">
							<div className="rounded-[12px] border border-amber-500/20 bg-amber-500/10 p-[8px]">
								<p className=" text-[12px] text-amber-500">{t('polygonWarning')}</p>
							</div>
							<div className="flex items-start gap-[8px] rounded-[12px] border border-blue-500/20 bg-blue-500/10 p-[8px]">
								<Info className="mt-px size-[14px] shrink-0 text-blue-400" />
								<p className="text-[12px] text-blue-400">
									{t('usdcSwapInfo')}{' '}
									<Link
										href="/emergency-swap"
										className="font-semibold underline"
										onClick={() => closeModal()}
									>
										{t('swapItHere')}
									</Link>
									.
								</p>
							</div>
						</div>
					)}
				</div>

				{/* View on Explorer */}
				<a
					href={explorerUrl}
					className="text-center font-normal text-[16px] text-brand-gray-secondary underline"
					target="_blank"
					rel="noreferrer"
				>
					{t('seeDepositHistory')}
				</a>
			</DialogContent>
		</Dialog>
	)
}
